import React, { ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { useStyles } from "./LoginStyle";
import CarradaleLogo from "../../shared/components/Icons/CarradaleLogo";
import { ApplicationState } from "../../app/rootReducer";
import { AppDispatch } from "index";
import { Button } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { MicrosoftLogo } from "../../shared/assets/MicrosoftLogo";
import { loginRequest } from "../../config";
import { microsoftAuthLogin, setTokens } from "app/auth/authSlice";

import InputWithValidation from "shared/components/Inputs/InputWithValidation";
import EmailIcon from "@mui/icons-material/AlternateEmail";
import PasswordIcon from "@mui/icons-material/Lock";
import { ILoginCredentials } from "shared/models/ILoginCredentials";
import { handleLogin } from "app/auth/authService";
import { show } from "shared/components/Toast/toastSlice";
import { useFormHook } from "shared/hooks/useForm";
import { noneRoutes } from "app/routes/byRole/enums/none";
import { showToast } from "shared/services/toastService";
import { theme } from "app/themes";
import { useLocation } from "react-router";

interface Props {}

export default function Auth(props: Props): ReactElement {
	const dispatch = useDispatch<AppDispatch>();
	const isLoggedIn = useSelector((state: ApplicationState) => state.auth.isLoggedIn);
	const history = useHistory();
	const classes = useStyles();
	const { formHook, formErrors, setFormErrors } = useFormHook<ILoginCredentials>();
	const { search } = useLocation();
	const params = new URLSearchParams(search);
	const token = params.get("token");

	const { instance } = useMsal();

	const handleAdLogin = (instance: IPublicClientApplication) => {
		instance
			.loginPopup(loginRequest())
			.then((loginResponse) => {
				dispatch(
					setTokens({
						accessToken: loginResponse.accessToken,
						expiresOn: loginResponse.expiresOn?.getTime(),
						refreshToken: "",
						type: "microsoft",
					})
				);
				dispatch(microsoftAuthLogin(token))
					.unwrap()
					.then((data) => {
						if (!data.redirect.url) {
							history.push("/");
						} else {
							const path = data.redirect.url.replace(window.location.origin, "");
							history.push(path);

							if (!!data.redirect.info) {
								showToast(data.redirect.info, "info");
							}
						}
					})
					.catch(() => {
						showToast("User was not found", "error");
					});
			})
			.catch((e) => {
				if (e.errorCode === "user_cancelled" || e.errorCode === "interaction_in_progress") return;
				showToast("A Microsoft error has occured.", "error");
			});
	};

	const onSubmit = formHook.handleSubmit((data: ILoginCredentials) => {
		if (!!token) {
			data.redirectToken = token;
		}

		handleLogin(data)
			.then((data) => {
				if (!data.redirect.url) {
					history.push("/");
				} else {
					const path = data.redirect.url.replace(window.location.origin, "");
					history.push(path);

					if (!!data.redirect.info) {
						showToast(data.redirect.info, "info");
					}
				}
			})
			.catch((e) => {
				if (e.errors?.["Toast"]) {
					dispatch(
						show({
							message: e.errors["Toast"],
							style: "error",
						})
					);
				}

				setFormErrors(e.errors);
			});
	});

	return (
		<div className={classes.root}>
			<div className={classes.formContainer}>
				<CarradaleLogo height={42} width={280} />
				<div className={classes.formHeader}>Login</div>
				<form className={classes.form}>
					<InputWithValidation
						type="email"
						name="username"
						label="Email"
						formHook={formHook}
						customErrors={formErrors}
						icon={<EmailIcon style={{ fill: theme.palette.text.primary }} />}
					/>
					<InputWithValidation
						name="password"
						formHook={formHook}
						customErrors={formErrors}
						icon={<PasswordIcon style={{ fill: theme.palette.text.primary }} />}
						type="password"
					/>
					<div className={classes.forgotPassword}>
						<Link to={noneRoutes.NEW_PASSWORD}>Forgot your password?</Link>
					</div>
					<Button className={classes.loginButton} onClick={onSubmit}>
						Login
					</Button>
				</form>

				<Button className={classes.loginButton} onClick={() => handleAdLogin(instance)}>
					<MicrosoftLogo color={theme.palette.text.primary} className={classes.microsoftLogo} />
					<span>Login with Microsoft</span>
				</Button>
			</div>
		</div>
	);
}
